import React from 'react';
import { Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '@/providers/auth-provider';
import type { AdminRole } from '@/types';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: string;
  role?: AdminRole;
  roles?: AdminRole[];
  fallback?: React.ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  role,
  roles,
  fallback,
}) => {
  // Bypass permission checks when VITE_SKIP_ADMIN_AUTH is enabled (for E2E testing)
  if (import.meta.env.VITE_SKIP_ADMIN_AUTH === 'true') {
    console.log('[PermissionGuard] VITE_SKIP_ADMIN_AUTH enabled - bypassing permission check for:', { permission, role, roles });
    return <>{children}</>;
  }

  const { user, hasPermission, hasRole } = useAuth();

  // Check if user has required permission
  if (permission && !hasPermission(permission)) {
    return fallback || <UnauthorizedAccess />;
  }

  // Check if user has required role
  if (role && !hasRole(role)) {
    return fallback || <UnauthorizedAccess />;
  }

  // Check if user has any of the required roles
  if (roles && !roles.some(r => hasRole(r))) {
    return fallback || <UnauthorizedAccess />;
  }

  return <>{children}</>;
};

const UnauthorizedAccess: React.FC = () => {
  return (
    <Result
      status="403"
      title="403"
      subTitle="Sorry, you are not authorized to access this page."
      icon={<LockOutlined />}
      extra={
        <Button type="primary" onClick={() => window.history.back()}>
          Go Back
        </Button>
      }
    />
  );
};

// Higher-order component for protecting routes
export const withPermission = (
  Component: React.ComponentType,
  permission?: string,
  role?: AdminRole,
  roles?: AdminRole[]
) => {
  return (props: any) => (
    <PermissionGuard permission={permission} role={role} roles={roles}>
      <Component {...props} />
    </PermissionGuard>
  );
};

// Utility component for conditional rendering based on permissions
interface CanProps {
  permission?: string;
  role?: AdminRole;
  roles?: AdminRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const Can: React.FC<CanProps> = ({
  permission,
  role,
  roles,
  children,
  fallback = null,
}) => {
  // Bypass permission checks when VITE_SKIP_ADMIN_AUTH is enabled (for E2E testing)
  if (import.meta.env.VITE_SKIP_ADMIN_AUTH === 'true') {
    console.log('[Can] VITE_SKIP_ADMIN_AUTH enabled - bypassing permission check for:', { permission, role, roles });
    return <>{children}</>;
  }

  const { hasPermission, hasRole } = useAuth();

  // Check permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Check role
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  // Check multiple roles
  if (roles && !roles.some(r => hasRole(r))) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};